import jsonLogic from 'json-logic-js';

export const getColorOfValue = (value, colors, points) => {
  if (colors.length === points.length + 1) {
    for (let i = points.length - 1; i >= 0; i--) {
      if (value > points[i]) {
        return colors[i + 1];
      }
    }
    return colors[0];
  } else {
    return "black";
  }
};

export const getQuestionResponseColor = (question, value) => {
  let color = "black";
  if (!question.no_color) {
    if (question.low_good) {
      if (value >= question.upper) {
        color = "red";
      } else if (value <= question.lower) {
        color = "green";
      } else {
        color = "orange";
      }
    } else {
      if (value >= question.upper) {
        color = "green";
      } else if (value <= question.lower) {
        color = "red";
      } else {
        color = "orange";
      }
    }
  }
  return color;
};

export const getTextColorBasedOnBg = (backgroundColor) => {
  if (backgroundColor) {
    // Convert the hex color to RGB
    const r = parseInt(backgroundColor.substring(1, 3), 16);
    const g = parseInt(backgroundColor.substring(3, 5), 16);
    const b = parseInt(backgroundColor.substring(5, 7), 16);

    // Calculate the luminance of the background color
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    // If the luminance is lower than a certain threshold, use white text; otherwise, use black text
    return luminance > 186 ? '#000000' : '#FFFFFF';
  }
}

export class DataColorUtil {
  constructor({teamName, gpsBaselines, teamGpsMetrics, colorStrategies, playerGpsBaselines = []}) {
    this.teamName = teamName;
    this.gpsBaselines = gpsBaselines;
    this.playerGpsBaselines = playerGpsBaselines;
    this.strategyVariables = ['std_dev', 'metric_baseline', 'one'];
    this.teamGpsMetrics = teamGpsMetrics;
    this.colorStrategies = colorStrategies;
  }

  _validateState() {
    /**
     * Validates that all required class members are present and valid
     * @returns {Object} - Object with isValid flag and error message if invalid
     */
    if (!this.teamName || typeof this.teamName !== 'string') {
      return { isValid: false, error: 'Invalid or missing teamName' };
    }
    
    if (!this.colorStrategies || !Array.isArray(this.colorStrategies) || this.colorStrategies.length === 0) {
      return { isValid: false, error: 'Invalid or missing colorStrategies' };
    }
    
    // For methods that use GPS metrics, we should have valid teamGpsMetrics
    if (!this.teamGpsMetrics || !Array.isArray(this.teamGpsMetrics || this.teamGpsMetrics.length === 0)) {
      return { isValid: false, error: 'Invalid or missing teamGpsMetrics' };
    }

    if (!this.gpsBaselines || typeof this.gpsBaselines !== 'object') {
      return { isValid: false, error: 'Invalid or missing gpsBaselines' };
    }

    return { isValid: true };
  }

  _getColorStrategy(metricIdentifier, strategyOverrideId = null) {

    if (strategyOverrideId) {
      const overrideStrategy = this.colorStrategies.find(s => s.id === strategyOverrideId);
      if (overrideStrategy) {
        return overrideStrategy;
      }
      console.warn(`No strategy found for strategyOverrideId ${strategyOverrideId}`);
      return null;
    }

    if (metricIdentifier && metricIdentifier.startsWith("gps-")) {
      try {
        const metricId = parseInt(metricIdentifier.split("-")[1]);

        for (const metric of this.teamGpsMetrics) {
          if (metric.metric_id === metricId && metric.color_strategy_id) {
            const strategy = this.colorStrategies.find(s => s.id === metric.color_strategy_id);
            if (strategy) {
              return strategy;
            }
          }
        }
      } catch (e) {
        console.error(`Error processing metricIdentifier ${metricIdentifier}: ${e.message}`);
      }
    }

    console.warn(`No strategy found for metricIdentifier ${metricIdentifier}`);
    return null;
  }

  _getCustomVariableValue(metricIdentifier, variable, baselineMultiplier = 1, playerId = null, inputValue = null) {
    /**
     * Returns the value of a custom variable for the given metricIdentifier
     * 
     * @param {string} metricIdentifier - The metric identifier
     * @param {string} variable - The variable name
     * @param {number} baselineMultiplier - Optional multiplier to apply to the baseline value
     * @param {string|number} playerId - Optional player ID for player-specific baselines
     * @param {number} inputValue - Optional input value
     * @returns {number} - The variable value
     */
    if (variable === "metric_baseline") {
      const metricKey = this._getMetricKey(metricIdentifier);
      
      // Use player-specific baselines on breakdown page
      if (playerId !== null && this.playerGpsBaselines?.length) {
        const playerBaseline = this.playerGpsBaselines.find(player => player.player_id === playerId)
        
        if (!playerBaseline) {
          console.warn(`Player baseline not found for playerId: ${playerId}`);
          return 0;
        }
        
        return parseFloat(playerBaseline[metricKey] || 0) * baselineMultiplier;
      } else {
        // Use regular baselines
        return parseFloat(this.gpsBaselines?.[metricKey] || 0) * baselineMultiplier;
      }
    }

    if (variable === "std_dev") {
      return 0;
    }

    if (variable === "one") {
      return 1;
    }

    console.warn(`No ${variable} found for metricIdentifier ${metricIdentifier}`);
    return 0;
  }

  _getMetricId(metricKey) {
    /**
     * Returns the metric ID from the metric key
     * 
     * @param {string} metricKey - The metric key to look up
     * @returns {string|null} - The metric ID if found, null otherwise
     */
    if (!metricKey || !this.teamGpsMetrics) {
      return null;
    }

    // Loop through teamGpsMetrics to find the matching metric by key
    for (const metric of this.teamGpsMetrics) {
      try {
        if (metric.key === metricKey) {
          return metric.metric_id;
        }
      } catch (e) {
        console.error(`Error accessing metric: ${e}`);
        continue;
      }
    }

    return null;
  }

  _getMetricKey(metricIdentifier) {
    /**
     * Returns the metric key from the metric identifier
     * 
     * @param {string} metricIdentifier - The metric identifier
     * @returns {string|null} - The metric key if found, null otherwise
     */
    if (!metricIdentifier) {
      return null;
    }

    // Extract the metric ID from the metric identifier
    try {
      const metricId = metricIdentifier.split("-")[1];
      
      if (!this.teamGpsMetrics) {
        console.warn(`No metrics found for team ${this.teamName}`);
        return null;
      }

      for (const metric of this.teamGpsMetrics) {
        try {
          if (metric.metric_id === parseInt(metricId)) {
            return metric.key;
          }
        } catch (e) {
          console.error(`Error accessing metric: ${e}`);
          continue;
        }
      }
    } catch (e) {
      console.warn(`Invalid metric identifier format: ${metricIdentifier}`);
      return null;
    }

    return null;
  }

  get_color({ metricIdentifier, inputValue, strategyOverrideId = null, baselineMultiplier = 1, playerId = null }) {
    /**
     * Returns the hex color based on the metricIdentifier and inputValue
     * 
     * @param {Object} params - The parameters object
     * @param {string} params.metricIdentifier - The metric identifier
     * @param {number} params.inputValue - The input value
     * @param {string} params.strategyOverrideId - Optional strategy id override
     * @param {number} params.baselineMultiplier - Optional multiplier to apply to the baseline value
     * @param {string|number} params.playerId - Optional player ID for player-specific baselines
     * @returns {string} - The hex color
     */
    // Validate state before proceeding
    const validation = this._validateState();
    if (!validation.isValid) {
      console.error(`DataColorUtil validation failed: ${validation.error}`);
      return "#CCCCCC"; // Return default color when invalid
    }
    
    // Get the strategy
    const strategy = this._getColorStrategy(metricIdentifier, strategyOverrideId);
    
    // If strategy is null, return default color
    if (!strategy) {
      console.warn(`Failed to get color strategy for ${metricIdentifier}. Returning #CCCCCC`);
      return "#CCCCCC";
    }

    let processedInputValue = inputValue || 0;

    const inputDict = {
      value: processedInputValue
    };

    // Add custom variables if needed
    try {
      for (const variable of this.strategyVariables) {
        if (strategy.logic && strategy.logic.includes(variable)) {
          inputDict[variable] = this._getCustomVariableValue(metricIdentifier, variable, baselineMultiplier, playerId, processedInputValue);
        }
      }

      // Parse the strategy logic and apply it
      const strategyJson = JSON.parse(strategy.logic);
      return jsonLogic.apply(strategyJson, inputDict);
    } catch (e) {
      console.error(`Error applying strategy: ${e.message}`);
      return "#CCCCCC";
    }
  }

  get_gps_color(metricKey, inputValue, baselineMultiplier = 1, playerId = null) {
    /**
     * Returns the hex color based on the gps metricKey and inputValue
     * 
     * @param {string} metricKey - The metric key
     * @param {number} inputValue - The input value
     * @param {number} baselineMultiplier - Optional multiplier to apply to the baseline value
     * @param {string|number} playerId - Optional player ID for player-specific baselines
     * @returns {string} - The hex color
     */
    // Validate state before proceeding
    const validation = this._validateState();
    if (!validation.isValid) {
      console.error(`DataColorUtil validation failed: ${validation.error}`);
      return "#CCCCCC"; // Return default color when invalid
    }
    
    // Handle null/undefined metricKey
    if (!metricKey) {
      console.warn("Null or undefined metricKey provided to get_gps_color");
      return "#CCCCCC";
    }
    
    try {
      let strategyOverrideId = null;
      
      if (metricKey.endsWith("_14dma_diff")) {
        strategyOverrideId = 7;
        return this.get_color({ metricIdentifier: null, inputValue, strategyOverrideId, baselineMultiplier, playerId });
      }
      
      if (metricKey === 'days_since_x_top_speed') {
        strategyOverrideId = this.teamName === 'Campbell University Football' ? 14 : 8;
        return this.get_color({ metricIdentifier: null, inputValue, strategyOverrideId, baselineMultiplier, playerId });
      }

      const metricId = this._getMetricId(metricKey);
      
      if (metricId === null) {
        console.warn(`Metric key ${metricKey} not found`);
        return "#CCCCCC";
      }
      
      const metricIdentifier = `gps-${metricId}`;
      
      return this.get_color({ metricIdentifier, inputValue, strategyOverrideId, baselineMultiplier, playerId });
    } catch (e) {
      console.error(`Error in get_gps_color: ${e.message}`);
      return "#CCCCCC";
    }
  }
}

// The backend lets only the creator edit or delete a color strategy. Default strategies have no creator.
export const getColorStrategyLockReason = (strategy, currentUserId) => {
  if (!strategy.creator_id) {
    return "You cannot change a default color strategy";
  }
  if (String(strategy.creator_id) !== String(currentUserId)) {
    return "Only the coach who created this color strategy can change it";
  }
  return null;
};
